import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Baby, Dog, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import API from '@/utils/api';

/**
 * AddProfileDialog - Create a new family profile
 */

const PROFILE_TYPES = [
  { 
    id: 'adult', 
    label: 'Adult', 
    icon: User, 
    color: '#EF4444',
    description: 'Yourself, spouse, or parent'
  },
  { 
    id: 'child', 
    label: 'Child', 
    icon: Baby, 
    color: '#3B82F6',
    description: 'Son, daughter, or dependent'
  },
  { 
    id: 'pet', 
    label: 'Pet', 
    icon: Dog, 
    color: '#22C55E',
    description: 'Dog, cat, or other pet'
  },
];

const RELATIONSHIPS = {
  adult: ['Self', 'Spouse', 'Partner', 'Parent', 'Grandparent', 'Other'],
  child: ['Son', 'Daughter', 'Grandchild', 'Nephew', 'Niece', 'Other'],
  pet: ['Dog', 'Cat', 'Bird', 'Fish', 'Hamster', 'Other'],
};

const AddProfileDialog = ({ open, onOpenChange, onProfileCreated }) => {
  const [step, setStep] = useState(1);
  const [profileType, setProfileType] = useState(null);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [saving, setSaving] = useState(false);

  const handleTypeSelect = (type) => {
    setProfileType(type);
    setStep(2);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    try {
      setSaving(true);
      const response = await API.post('/emergency-binder/profiles', {
        name: name.trim(),
        profile_type: profileType.id,
        relationship: relationship || null,
        avatar_color: profileType.color,
      });
      
      onProfileCreated(response.data);
      
      // Reset form
      setStep(1);
      setProfileType(null);
      setName('');
      setRelationship('');
    } catch (error) {
      console.error('Failed to create profile:', error);
      toast.error('Failed to create profile');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setProfileType(null);
    setName('');
    setRelationship('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? 'Add New Profile' : `Add ${profileType?.label}`}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          /* Step 1: Select profile type */
          <div className="space-y-3 py-4">
            <p className="text-sm text-slate-600 mb-4">
              Who is this profile for?
            </p>
            {PROFILE_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => handleTypeSelect(type)}
                  className={cn(
                    "w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all",
                    "hover:border-slate-300 hover:bg-slate-50"
                  )}
                  data-testid={`profile-type-${type.id}`}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                    style={{ backgroundColor: type.color }}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-slate-800">{type.label}</div>
                    <div className="text-sm text-slate-500">{type.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          /* Step 2: Enter details */
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              {profileType && (
                <>
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                    style={{ backgroundColor: profileType.color }}
                  >
                    <profileType.icon className="h-5 w-5" />
                  </div>
                  <span className="font-medium text-slate-700">{profileType.label} Profile</span>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile-name">Name</Label>
              <Input
                id="profile-name"
                placeholder={profileType?.id === 'pet' ? "Pet's name" : "Enter name"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12"
                data-testid="profile-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Relationship (Optional)</Label>
              <div className="flex flex-wrap gap-2">
                {RELATIONSHIPS[profileType?.id]?.map((rel) => (
                  <button
                    key={rel}
                    onClick={() => setRelationship(rel)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-sm border transition-all",
                      relationship === rel
                        ? "bg-red-100 border-red-300 text-red-700"
                        : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                    )}
                  >
                    {rel}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!name.trim() || saving}
                className="flex-1 bg-red-600 hover:bg-red-700"
                data-testid="create-profile-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Profile'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddProfileDialog;
